/**
 * Order detail, rebuilt from scratch on the template's own pieces:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-view.tsx
 *   the 8/4 main-and-side grid, items followed by the balance, the side column's cards;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx
 *   the hero's gradient cover band with the icon tile overlapping it;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/stats.tsx
 *   the solid colour icon tiles on the four figure cards;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/tracking-overview.tsx
 *   copy-the-number, and the bordered "latest update" callout under the tracker;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   icon tile + label/value blocks in the customer and delivery card;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/social-media/dashboard/recent-activities.tsx
 *   the activity feed rows.
 *
 * The tracker is rizzui's own `Stepper`; tables, badges, alerts, avatars, dropdowns and tooltips are
 * rizzui's; `MetricCard`, `WidgetCard`, `ReasonDialog`, `getStatusBadge` are `@steelyard/theme`'s.
 * The screen-to-template mapping is specs/017-isomorphic-theme-only/contracts/screen-map.md.
 *
 * Rules this screen keeps:
 * - Every figure is the API's (constitution I). Nothing is summed, subtracted or compared into a new
 *   number — the tracker's position is read from `ORDER_STATES`' own order, and a line is "short"
 *   only by the same ordered-vs-packed comparison the previous screen made.
 * - `getStatusBadge` is still the only colour source for an order state. The tracker's colours mark
 *   position (done / now / stopped), and the flags' colours mark severity — never the state itself.
 * - Reject, cancel and the short-fulfilment decision still go through `?act=` + `ReasonDialog`, so
 *   the list page's `OrderStateMenu` links keep working. Approve stays one click, as it is there.
 * - D-39's trade discount is `?act=discount` + `ReasonDialog` too, offered only to `orders.discount`
 *   (Super Admin) on a B2B order before it is packed. Every figure it moves — the discount, the GST
 *   on the discounted value, the total — is the API's answer, read back after the save.
 * - The customer record comes from the customers service and is not awaited: the order renders the
 *   moment it arrives, and a customer read the caller may not make (or that fails) says so in its
 *   own card instead of taking the page down (constitution VI).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  PiArrowRightBold as ArrowRight,
  PiCalendarBlankDuotone as CalendarIcon,
  PiCaretDownBold as CaretDown,
  PiCheckBold as Check,
  PiCheckCircleDuotone as ApprovedIcon,
  PiClockCounterClockwiseDuotone as HistoryIcon,
  PiCreditCardDuotone as CreditCardIcon,
  PiCurrencyInrDuotone as RupeeIcon,
  PiDotsThreeOutlineVerticalFill as MoreIcon,
  PiEnvelopeSimpleDuotone as MailIcon,
  PiHourglassMediumDuotone as InFlightIcon,
  PiInfoDuotone as InfoIcon,
  PiLockSimpleDuotone as LockIcon,
  PiMapPinDuotone as MapPinIcon,
  PiPackageDuotone as PackageIcon,
  PiPencilSimpleBold as EditIcon,
  PiPercentDuotone as DiscountIcon,
  PiProhibitDuotone as RejectIcon,
  PiReceiptDuotone as ReceiptIcon,
  PiScalesDuotone as ScalesIcon,
  PiSignatureDuotone as SignatureIcon,
  PiStorefrontDuotone as ChannelIcon,
  PiTruckDuotone as TruckIcon,
  PiUserCircleDuotone as UserIcon,
  PiUserPlusDuotone as PackerIcon,
  PiWarningDuotone as WarningIcon,
  PiXCircleDuotone as CancelIcon,
} from "react-icons/pi";
import { Alert, Avatar, Badge, Button, Dropdown, Input, Stepper, Table, Text } from "rizzui";
import { GRID_TABLE } from "../../components/table/table-view";
import toast from "react-hot-toast";
import { useConfirm } from "../../components/confirm-modal";
import { stepsFirst } from "../../components/orders/order-state-menu";
import { Link, useLoaderData, useNavigate, useOutletContext, useRevalidator, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { EmptyState, ErrorSurface, Field, MetricCard, ReasonDialog, WidgetCard, cn, getStatusBadge } from "@steelyard/theme";
import { SelectField } from "../../components/select-field";
import { TileBoundary } from "../../components/dashboard/TileBoundary";
import { CardTitle, InfoBlock, PageHero, StatusPill, iconMenuTriggerClass, menuTriggerClass } from "../../components/detail-parts";
import { problemMessage } from "../../components/dashboard/tone";
import { formatDate, formatMoment, formatMoney, sentenceCase } from "../../lib/format";
import { stateName } from "../../lib/invoice-model";
import { ApiError } from "../../lib/api";
import {
  advanceOrder,
  amendLines,
  approveOrder,
  beforePacked,
  cancelOrder,
  changeShippingAddress,
  decideShortFulfilment,
  money,
  nextAdvanceTargets,
  orderByKey,
  deliverySignatureHash,
  overrideOrderState,
  overrideTargets,
  packRecorded,
  pickingBegun,
  rejectOrder,
  setOrderDiscount,
  stateLabel,
  stepsBefore,
  type OrderDetail,
} from "../../lib/services/ordering";
import { addressTypeLabel, customerStatusLabel, getCustomer, type CustomerAddress, type CustomerDetail } from "../../lib/services/customers";
import { assignPackingTask, getOversightTasks, personName, type PackingTaskRow } from "../../lib/services/warehouse";
import { listPackers, type UserSummary } from "../../lib/services/identity";
import { finaliseInvoice, getInvoice, listInvoices, money as invoiceMoney, type InvoiceDetail } from "../../lib/services/invoicing";
import { imageUrl } from "@steelyard/common";
import { can } from "../../lib/permissions";
import { completeStepsPath, customerPath, invoicePath, orderPath } from "../../lib/paths";
import { canCompleteFrom } from "../../lib/complete-order";
import type { AccountSummary, PermissionState } from "../../types";

export async function orderDetailLoader({ params, request }: LoaderFunctionArgs) {
  const order = await orderByKey(request, params.orderNumber!);
  // D-40: a packed order's invoice may be a draft Billing holds — Ordering hears of it only once it is
  // finalised. Read whole (its totals) so it is checked and finalised from this page. null
  // when there is no draft; undefined when Billing could not be read (no billing.read, Billing down),
  // which shows nothing rather than a guess.
  const draft =
    order.state === "packed" && !order.invoiceId
      ? await listInvoices({ page: 1, pageSize: 1, orderIds: [order.id] })
          .then((r) => (r.items[0]?.reviewPending ? getInvoice(r.items[0].id) : null))
          .catch(() => undefined)
      : undefined;
  // An In-House delivery's signature is on the dispatch view, not the order: the hash of its image in
  // Files. undefined when that view could not be read, which the page says rather than hiding it.
  const signature = order.delivery ? await deliverySignatureHash(order.id).catch(() => undefined) : null;
  // Deliberately not awaited — see the file header. `TileBoundary` resolves it on its own.
  // The approver's name rides on the order itself (approvedByName) — no Identity read here.
  return { order, customer: getCustomer(order.customerId), draft, signature };
}

const OUTCOME_LABEL: Record<string, string> = {
  rejected: "Order rejected",
  cancelled: "Order cancelled",
  approved: "Order approved",
  held: "Held — no invoice raised",
  "shipped-short": "Shipped short",
  advanced: "Order state updated",
  amended: "Quantities saved",
  discounted: "Trade discount updated",
  "address-changed": "Shipping address changed",
  "invoice-finalised": "Invoice finalised",
};

export default function OrderDetailPage() {
  const loaded = useLoaderData() as {
    order: OrderDetail;
    customer: Promise<CustomerDetail>;
    draft: InvoiceDetail | null | undefined;
    signature: string | null | undefined;
  };
  const { order, customer, signature } = loaded;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const revalidator = useRevalidator();
  const act = searchParams.get("act");
  const done = searchParams.get("done");
  const refusalTitle = searchParams.get("refusalTitle");
  const refusalDetail = searchParams.get("refusalDetail");

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);

  const mayApprove = can(permissionState, "orders.administer");
  const mayWrite = can(permissionState, "orders.administer");
  // Advancing a state is its own permission (spec §5), not orders.administer.
  const mayAdvance = can(permissionState, "orders.advance");
  const begun = pickingBegun(order.state);
  const open = order.state !== "cancelled";

  const canApprove = order.state === "awaiting_approval" && mayApprove;
  const moves = open && mayAdvance ? nextAdvanceTargets(order.state) : [];
  // Super Admin: every other state, outside the lifecycle table.
  const forced = can(permissionState, "orders.override")
    ? overrideTargets(order.state).filter((s) => !(moves as readonly string[]).includes(s))
    : [];
  const confirm = useConfirm();
  const force = (target: string) => {
    const facts = { packRecorded: packRecorded(order), invoiced: order.invoiceId !== null };
    const steps = stepsBefore(order.state, facts, target);
    const complete = canCompleteFrom(order.state) ? () => navigate(completeStepsPath(order.orderNumber, target)) : undefined;
    if (steps.length > 0) return confirm(stepsFirst(order.orderNumber, target, steps, facts, complete));
    confirm({
      title: `Force ${order.orderNumber} to ${stateLabel(target)}?`,
      consequence:
        "This skips the normal order flow. Only the state changes — stock, packing and invoices are not touched.",
      confirmLabel: `Set to ${stateLabel(target)}`,
      reason: "Reason",
      onConfirm: (reason) => void run(() => overrideOrderState(order.id, target, reason), "advanced"),
    });
  };
  // A packing task exists from approval until the pack completes.
  const canAssignPacker = ["approved", "picking_packing"].includes(order.state) && can(permissionState, "packing.oversight");
  const canReject = order.state === "awaiting_approval" && mayWrite;
  const canSettleShort = order.shortFulfilmentPending && mayWrite;
  // OrderStateMachine: invoiced, dispatched, delivered and cancelled have no edge to cancelled.
  const canCancel = !["invoiced", "dispatched", "delivered", "cancelled"].includes(order.state) && mayWrite;
  // FR-052: the address is fixed once a packer is on it (picking_packing). The API refuses the rest.
  const canChangeAddress = open && !begun && mayWrite;
  const changeAddress = canChangeAddress
    ? (addressId: string, pincode: string) => run(() => changeShippingAddress(order.id, addressId, pincode), "address-changed")
    : undefined;
  // D-39: a Super Admin's trade discount — B2B only, and only before the pack completes (the invoice
  // is raised from the packed order). The API refuses anything else; this only decides what to offer.
  const canDiscount =
    can(permissionState, "orders.discount") && order.orderType === "credit" && beforePacked(order.state) && !order.invoiceId;
  // D-40, highlighted on a packed order: the invoice page's Finalise, for Billing's draft (billing.review).
  const draft = loaded.draft ?? null;
  const canFinalise = draft !== null && can(permissionState, "billing.review");
  // Drafts raised before Billing invoiced the delivery charge (D-31, 12 Oct) carry none; Billing's
  // finalise brings it in line with the order's. Two API figures compared to say so — nothing derived.
  const deliveryMissing = draft !== null && (money(order.deliveryCharge) ?? 0) > 0 && !draft.deliveryCharge;
  const finalise = (invoice: InvoiceDetail) =>
    confirm({
      title: `Finalise ${invoice.invoiceNumber}?`,
      consequence: (
        <>
          The invoice is released as it stands now: registered for an IRN where e-invoicing applies, queued for Tally, and posted to
          the customer&apos;s ledger, and this order moves to Invoiced. Its discount and round off can no longer be changed.
          {deliveryMissing ? (
            <strong className="mt-2 block text-orange-dark">
              This draft carries no delivery charge. Finalising adds the {formatMoney(money(order.deliveryCharge))} the order charged
              for delivery, so the invoice total goes up by that much.
            </strong>
          ) : null}
        </>
      ),
      confirmLabel: "Finalise the invoice",
      onConfirm: () => void run(() => finaliseInvoice(invoice.id), "invoice-finalised"),
    });
  const discountPercent = money(order.discountPercent) ?? 0;
  const discountTotal = money(order.discountTotal) ?? 0;
  const openAct = (name: string) => navigate(`${orderPath(order.orderNumber)}?act=${name}`);

  // Closed dialogs are not mounted (see the dialogs below): ReasonDialog's textarea id is fixed, so
  // two mounted at once would share one id and the open one's label could point at a hidden field.
  const [reasonError, setReasonError] = useState<string>();
  const withReason = (fd: FormData, go: (reason: string) => void) => {
    const reason = String(fd.get("reason") ?? "").trim();
    if (!reason) return setReasonError("A reason is required.");
    setReasonError(undefined);
    go(reason);
  };

  const back = (
    <Button type="button" variant="text" size="sm" onClick={() => { setReasonError(undefined); setSearchParams((p) => (p.delete("act"), p)); }}>
      Cancel
    </Button>
  );

  /** True when the change was applied — lets a form stay open (with what was typed) when it was refused. */
  async function run(fn: () => Promise<unknown>, outcome: string): Promise<boolean> {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      // Two moves in a row leave the URL identical (`?done=advanced`), which does not reliably re-run the
      // loader — the page then keeps offering the state it just left. Reload the order explicitly.
      revalidator.revalidate();
      setSearchParams({ done: outcome });
      toast.success(OUTCOME_LABEL[outcome] ?? "Done");
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const params: Record<string, string> = { done: "refused" };
        if (err.title) params.refusalTitle = err.title;
        if (err.detail) params.refusalDetail = err.detail;
        setSearchParams(params);
        toast.error(err.title ?? "Infra Junction refused that");
      } else {
        setActionError(err);
        toast.error("Could not apply that change");
      }
      return false;
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="@container flex flex-col gap-6">
      {/* ── Hero: identity, state and every action, in one place ─────────────────────────── */}
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Orders", href: "/orders" }, { name: order.orderNumber }]}
        back={{ href: "/orders", label: "All orders" }}
        icon={<ReceiptIcon aria-hidden className="size-9" />}
        title={order.orderNumber}
        copyValue={order.orderNumber}
        meta={
              <>
                <StatusPill status={order.state} />
                <Badge variant="flat" color={order.orderType === "credit" ? "secondary" : "info"} rounded="pill" className="gap-1.5">
                  <CreditCardIcon aria-hidden className="size-4" />
                  {order.orderType === "credit" ? "Credit · B2B" : "Prepaid · B2C"}
                </Badge>
                {discountTotal > 0 ? (
                  <Badge variant="flat" color="success" rounded="pill" className="gap-1.5">
                    <DiscountIcon aria-hidden className="size-4" />
                    {String(discountPercent)}% trade discount
                  </Badge>
                ) : null}
                {order.channel ? (
                  <Badge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-700">
                    <ChannelIcon aria-hidden className="size-4" />
                    {sentenceCase(order.channel)}
                  </Badge>
                ) : null}
                <Text as="span" className="inline-flex items-center gap-1.5 text-sm text-gray-500">
                  <CalendarIcon aria-hidden className="size-4" />
                  Placed {formatMoment(order.placedAt)}
                </Text>
              </>
        }
        actions={
          <>
            {canAssignPacker ? <PackerMenu orderId={order.id} orderNumber={order.orderNumber} /> : null}

            {moves.length > 0 || forced.length > 0 ? (
              <Dropdown placement="bottom-end">
                <Dropdown.Trigger
                  disabled={busy}
                  aria-label={`Move ${order.orderNumber} to another state (now ${stateLabel(order.state)})`}
                  className={menuTriggerClass}
                >
                  <TruckIcon aria-hidden className="size-4 text-secondary" />
                  Move to
                  <CaretDown aria-hidden className="size-3 text-gray-400" />
                </Dropdown.Trigger>
                <Dropdown.Menu className="max-h-96 w-64 overflow-y-auto">
                  {moves.length > 0 ? (
                    <Text className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Move this order to</Text>
                  ) : null}
                  {moves.map((target) => (
                    <Dropdown.Item key={target} onClick={() => run(() => advanceOrder(order.id, target), "advanced")} className="gap-2.5 text-sm">
                      <ArrowRight aria-hidden className="size-4 shrink-0 text-secondary" />
                      {stateLabel(target)}
                    </Dropdown.Item>
                  ))}
                  {forced.length > 0 ? (
                    <>
                      <Text className="border-t border-muted px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Any state (Super Admin)</Text>
                      {forced.map((target) => (
                        <Dropdown.Item key={target} onClick={() => force(target)} className="gap-2.5 text-sm">
                          <ArrowRight aria-hidden className="size-4 shrink-0 text-orange-dark" />
                          {stateLabel(target)}
                        </Dropdown.Item>
                      ))}
                    </>
                  ) : (
                    <Text className="border-t border-muted px-3 pb-1 pt-2 text-xs text-gray-500">Invoiced is not offered — only Billing makes that move.</Text>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            ) : null}

            {canApprove ? (
              <Button type="button" isLoading={busy} onClick={() => run(() => approveOrder(order.id), "approved")}>
                <Check aria-hidden className="me-1.5 size-4" />
                Approve order
              </Button>
            ) : null}

            {canSettleShort || canReject || canCancel || canDiscount ? (
              <Dropdown placement="bottom-end">
                <Dropdown.Trigger
                  disabled={busy}
                  aria-label={`More actions for ${order.orderNumber}`}
                  className={iconMenuTriggerClass}
                >
                  <MoreIcon aria-hidden className="size-4" />
                </Dropdown.Trigger>
                <Dropdown.Menu className="w-60">
                  {canDiscount ? (
                    <Dropdown.Item onClick={() => openAct("discount")} className="gap-2.5 text-sm">
                      <DiscountIcon aria-hidden className="size-4 shrink-0 text-green-dark" />
                      {discountTotal > 0 ? "Change trade discount…" : "Apply trade discount…"}
                    </Dropdown.Item>
                  ) : null}
                  {canDiscount && (canSettleShort || canReject || canCancel) ? <div role="separator" className="my-1 border-t border-muted" /> : null}
                  {canSettleShort ? (
                    <Dropdown.Item onClick={() => openAct("short")} className="gap-2.5 text-sm">
                      <ScalesIcon aria-hidden className="size-4 shrink-0 text-orange-dark" />
                      Settle short fulfilment…
                    </Dropdown.Item>
                  ) : null}
                  {canSettleShort && (canReject || canCancel) ? <div role="separator" className="my-1 border-t border-muted" /> : null}
                  {canReject ? (
                    <Dropdown.Item onClick={() => openAct("reject")} className="gap-2.5 text-sm text-red-dark">
                      <RejectIcon aria-hidden className="size-4 shrink-0" />
                      Reject…
                    </Dropdown.Item>
                  ) : null}
                  {canCancel ? (
                    <Dropdown.Item onClick={() => openAct("cancel")} className="gap-2.5 text-sm text-red-dark">
                      <CancelIcon aria-hidden className="size-4 shrink-0" />
                      Cancel order…
                    </Dropdown.Item>
                  ) : null}
                </Dropdown.Menu>
              </Dropdown>
            ) : null}
          </>
        }
      />

      {actionError ? <ErrorSurface error={actionError} action="apply that change" /> : null}
      {done === "refused" ? (
        <ErrorSurface error={{ title: refusalTitle ?? "Infra Junction refused that", status: 409, detail: refusalDetail ?? undefined }} action="apply that change" />
      ) : null}
      {done && done !== "refused" ? (
        <div role="status">
          <Alert color="success" variant="flat" bar icon={<ApprovedIcon aria-hidden className="size-5" />}>
            Done — {OUTCOME_LABEL[done] ?? "done"}.
          </Alert>
        </div>
      ) : null}

      {mayWrite ? (
        <>
          {act === "reject" && canReject ? (
          <ReasonDialog
            error={reasonError}
            title={`Reject ${order.orderNumber}`}
            consequence={<>The order is cancelled, the reservation released and the customer notified. This cannot be undone — they would have to place it again.</>}
            action={(fd: FormData) => withReason(fd, (reason) => run(() => rejectOrder(order.id, reason), "rejected"))}
            confirmLabel={`Reject ${order.orderNumber}`}
            cancel={back}
            reasonLabel="Why is this order being rejected?"
            reasonHint="Recorded immutably and shown to the customer. Infra Junction refuses a rejection with no reason."
          />
          ) : null}
          {act === "cancel" && canCancel ? (
          <ReasonDialog
            error={reasonError}
            title={`Cancel ${order.orderNumber}`}
            consequence={
              <>
                The reservation is released and the order closed.
                {order.paymentStatus === "paid" ? " This order is paid, so a full refund is raised and the order stays open until the refund resolves." : ""}
                {order.invoiceNumber ? " An invoice has already been raised against it, so Infra Junction may refuse this and point at the credit-note route instead." : ""}
              </>
            }
            action={(fd: FormData) => withReason(fd, (reason) => run(() => cancelOrder(order.id, reason), "cancelled"))}
            confirmLabel={`Cancel ${order.orderNumber}`}
            cancel={back}
            reasonLabel="Why is this order being cancelled?"
            reasonHint="Required of an administrator, recorded immutably."
          />
          ) : null}
          {act === "short" && canSettleShort ? (
          <ReasonDialog
            error={reasonError}
            title={`Settle the short fulfilment on ${order.orderNumber}`}
            consequence={<ShortFulfilmentConsequence order={order} />}
            action={(fd: FormData) => {
              const decision = String(fd.get("decision") ?? "");
              if (decision !== "ship_short" && decision !== "hold") return;
              withReason(fd, (reason) => run(() => decideShortFulfilment(order.id, decision, reason), decision === "hold" ? "held" : "shipped-short"));
            }}
            confirmLabel="Apply this decision"
            cancel={back}
            reasonLabel="Why?"
            reasonHint="Recorded against the order. The customer's invoice or refund follows from this."
          >
            <SelectField label="Decision" name="decision" defaultValue="ship_short" required>
              <option value="ship_short">Ship short — invoice at the packed quantity</option>
              <option value="hold">Hold — raise no invoice</option>
            </SelectField>
          </ReasonDialog>
          ) : null}
        </>
      ) : null}

      {canDiscount && act === "discount" ? (
        <ReasonDialog
          error={reasonError}
          title={`Trade discount on ${order.orderNumber}`}
          consequence={
            <>
              Taken off every line <strong>before GST</strong>, so the tax is charged on the discounted value, and printed on the
              invoice as its own line. Billing applies the same percentage to the packed quantity when it raises the invoice.
              Enter 0 to remove it. It can be changed until the order is packed; after that, a credit note is the route.
            </>
          }
          action={(fd: FormData) => {
            const percent = Number(String(fd.get("discountPercent") ?? "").trim());
            if (!Number.isFinite(percent)) return;
            withReason(fd, (reason) => run(() => setOrderDiscount(order.id, percent, reason), "discounted"));
          }}
          confirmLabel="Save the discount"
          cancel={back}
          reasonLabel="Why is this discount being given?"
          reasonHint="Recorded on the order's audit trail with the before and after totals."
        >
          <Field
            label="Discount (%)"
            name="discountPercent"
            type="number"
            inputMode="decimal"
            min="0"
            max="99.99"
            step="0.01"
            required
            defaultValue={String(discountPercent)}
            hint="0 to under 100. The discount amount, the GST and the new total are worked out by Infra Junction, not here."
          />
        </ReasonDialog>
      ) : null}

      {/* The draft's figures as Billing sent them, in one strip, so it is finalised here without
          opening the invoice. A tax head is shown only when Billing charged it — nothing is summed. */}
      {draft ? (
        <Alert color="warning" variant="flat" bar icon={<ReceiptIcon aria-hidden className="size-5" />}>
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
            <div className="min-w-0">
              <Text className="font-semibold text-orange-dark">Draft invoice {draft.invoiceNumber} — awaiting finalise</Text>
              <Text className="mt-0.5 text-sm text-gray-600">
                Dated {formatDate(draft.invoiceDate)} · {draft.lines.length} {draft.lines.length === 1 ? "line" : "lines"} · billed at the
                packed quantity, not the ordered one · not registered, in Tally or on the ledger until finalised
              </Text>
              {deliveryMissing ? (
                <Text className="mt-1 flex items-center gap-1.5 text-sm font-medium text-orange-dark">
                  <WarningIcon aria-hidden className="size-4 shrink-0" />
                  Raised before delivery was invoiced, so it has none yet — finalising adds the order&rsquo;s{" "}
                  {formatMoney(money(order.deliveryCharge))} delivery charge.
                </Text>
              ) : null}
            </div>
            <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
              {(
                [
                  ["Taxable", invoiceMoney(draft.taxableValue)],
                  draft.discount ? ["Discount", `−${invoiceMoney(draft.discount)}`] : null,
                  draft.cgst ? ["CGST", invoiceMoney(draft.cgst)] : null,
                  draft.sgst ? ["SGST", invoiceMoney(draft.sgst)] : null,
                  draft.igst ? ["IGST", invoiceMoney(draft.igst)] : null,
                  draft.deliveryCharge ? ["Delivery", invoiceMoney(draft.deliveryCharge)] : null,
                  draft.roundOff ? ["Round off", invoiceMoney(draft.roundOff)] : null,
                  ["Invoice total", draft.grandTotalDisplay],
                ] as const
              )
                .filter((figure) => figure !== null)
                .map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs text-gray-500">{label}</dt>
                    <dd className={cn("tabular-nums", label === "Invoice total" ? "font-bold text-gray-900" : "font-medium text-gray-700")}>{value}</dd>
                  </div>
                ))}
            </dl>
            <div className="flex items-center gap-3">
              <Link to={invoicePath(draft.invoiceNumber)} className="text-sm font-medium text-blue-dark hover:underline">
                Full invoice
              </Link>
              {canFinalise ? (
                <Button type="button" size="sm" isLoading={busy} onClick={() => finalise(draft)}>
                  <ApprovedIcon aria-hidden className="me-1.5 size-4" />
                  Finalise invoice
                </Button>
              ) : (
                <Text className="text-sm text-gray-600">A Super Admin finalises it.</Text>
              )}
            </div>
          </div>
        </Alert>
      ) : null}

      <OrderProgress order={order} />

      <OrderFlags order={order} onSettle={canSettleShort ? () => openAct("short") : undefined} />

      {/* ── Figures ──────────────────────────────────────────────────────────────────────── */}
      <div className="grid gap-4 @lg:grid-cols-2 @6xl:grid-cols-4">
        <MetricCard
          title="Order total"
          metric={formatMoney(money(order.grandTotal))}
          icon={<RupeeIcon aria-hidden className="size-6" />}
          iconClassName="bg-primary text-white shadow-sm"
          info={
            <Text className="mt-0.5 text-xs text-gray-500">
              as ordered · of which GST {formatMoney(money(order.taxTotal))}
              {discountTotal > 0 ? <> · after {formatMoney(discountTotal)} discount</> : null}
            </Text>
          }
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Order lines"
          metric={`${order.lines.length} ${order.lines.length === 1 ? "product" : "products"}`}
          icon={<PackageIcon aria-hidden className="size-6" />}
          iconClassName="bg-secondary text-white shadow-sm"
          info={
            <Text className="mt-0.5 text-xs text-gray-500">
              {begun ? "Locked — picking has begun" : "Quantities can still change"}
            </Text>
          }
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Payment"
          metric={order.orderType === "credit" ? "On credit" : "Prepaid"}
          icon={<CreditCardIcon aria-hidden className="size-6" />}
          iconClassName="bg-green text-white shadow-sm"
          info={<div className="mt-1">{order.paymentStatus ? getStatusBadge(order.paymentStatus) : <Text className="text-xs text-gray-500">No payment recorded</Text>}</div>}
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
        <MetricCard
          title="Invoice"
          metric={order.invoiceNumber ?? draft?.invoiceNumber ?? "Not raised yet"}
          icon={<ReceiptIcon aria-hidden className="size-6" />}
          iconClassName="bg-blue text-white shadow-sm"
          info={
            order.invoiceNumber ? (
              <Text className="mt-0.5 text-xs text-gray-500">
                Invoice total {formatMoney(money(order.invoicedTotal))}
                {order.invoiceNumber && can(permissionState, "billing.read") ? (
                  <>
                    {" · "}
                    <Link to={invoicePath(order.invoiceNumber)} className="font-medium text-blue-dark hover:underline">
                      Open invoice
                    </Link>
                  </>
                ) : null}
              </Text>
            ) : draft ? (
              <Text className="mt-0.5 text-xs text-orange-dark">Invoice total {draft.grandTotalDisplay} · draft, awaiting finalise</Text>
            ) : (
              <Text className="mt-0.5 text-xs text-gray-500">Raised by Billing when packing completes</Text>
            )
          }
          rounded="lg"
          className="transition-shadow hover:shadow-md"
        />
      </div>

      {/* ── Work area. On a narrow screen the two columns dissolve into one list, re-ordered so the
             total and the customer sit right under the items instead of after the whole history. */}
      <div className="flex flex-col gap-6 @5xl:grid @5xl:grid-cols-12 @5xl:items-start">
        <div className="contents @5xl:col-span-8 @5xl:flex @5xl:flex-col @5xl:gap-6">
          <Lines
            order={order}
            editable={!begun && mayWrite}
            busy={busy}
            onSave={(lines) => mayWrite && run(() => amendLines(order.id, lines), "amended")}
            lockedReason={!mayWrite ? "You don't have permission to amend order lines." : undefined}
          />
          <div className="order-last">
            <ActivityFeed order={order} />
          </div>
        </div>

        <div className="contents @5xl:col-span-4 @5xl:flex @5xl:flex-col @5xl:gap-6">
          <OrderSummary order={order} draft={draft} />
          <TileBoundary
            resolve={customer}
            fallbackClassName="h-[360px]"
            errorFallback={(error) => (
              <CustomerCard order={order} customer={null} error={error} signature={signature} />
            )}
          >
            {(record) => (
              <CustomerCard order={order} customer={record} signature={signature} onChangeAddress={changeAddress} />
            )}
          </TileBoundary>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Building blocks
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/* ── Packer ───────────────────────────────────────────────────────────────────────────────── */

/**
 * §17 "reassign": an order has no owner field to reassign — its packing task does. The task comes from
 * Warehouse and the packers from Identity (`GET /api/identity/users/packers`), both read here; picking a
 * name assigns the task, and picking another moves it — Warehouse's assign accepts an assigned task.
 */
export function PackerMenu({ orderId, orderNumber }: { orderId: string; orderNumber: string }) {
  // undefined while loading; null when there is no task, or it could not be read (`note` says which).
  const [task, setTask] = useState<PackingTaskRow | null>();
  const [note, setNote] = useState("");
  const [packers, setPackers] = useState<UserSummary[] | null>();
  const [pending, setPending] = useState(false);

  useEffect(() => {
    // ponytail: Warehouse has no task-by-order read, so this takes the unpaged task list (the read
    // /packing repeats every 30 s) and picks this order's task. Add `?orderId=` there if it grows.
    getOversightTasks()
      .then((all) => {
        const found = all.find((t) => t.orderId === orderId) ?? null;
        setTask(found);
        if (!found)
          setNote("This order has no packing task yet. One opens when the order is approved — if it was approved and has none, Warehouse could not be reached then.");
      })
      .catch((err: unknown) => {
        setTask(null);
        setNote(problemMessage(err, "The packing task could not be read."));
      });
    listPackers()
      .then((page) => setPackers(page.items))
      .catch(() => setPackers(null));
  }, [orderId]);

  async function assign(person: UserSummary) {
    if (!task) return;
    setPending(true);
    try {
      const assigned = await assignPackingTask(task.id, person.id);
      setTask(assigned);
      toast.success(`${orderNumber} assigned to ${personName(assigned.assignedToName)}`);
    } catch (err) {
      toast.error(problemMessage(err, "Could not assign this order's packing task"));
    } finally {
      setPending(false);
    }
  }

  const current = task?.assignedTo ? personName(task.assignedToName) : null;
  const finished = task?.status === "complete" || task?.status === "cancelled";
  const message = (text: string) => <Text className="px-3 py-2 text-sm text-gray-500">{text}</Text>;

  return (
    <Dropdown placement="bottom-end">
      <Dropdown.Trigger
        disabled={pending || task === undefined}
        aria-label={current ? `Packer for ${orderNumber}: ${current}. Reassign` : `Assign a Packer to ${orderNumber}`}
        className={menuTriggerClass}
      >
        <PackerIcon aria-hidden className="size-4 text-secondary" />
        {task === undefined ? "Loading…" : pending ? "Assigning…" : (current ?? "Assign Packer")}
        <CaretDown aria-hidden className="size-3 text-gray-400" />
      </Dropdown.Trigger>
      <Dropdown.Menu className="max-h-96 w-72 overflow-y-auto">
        {!task ? (
          message(note)
        ) : finished ? (
          message(`This packing task is ${task.status}, so it can no longer be reassigned.`)
        ) : packers === undefined ? (
          message("Loading packers…")
        ) : packers === null ? (
          message("The list of packers could not be loaded, so this order cannot be assigned here.")
        ) : packers.length === 0 ? (
          message("Nobody active holds the Packer role. Add someone under Users.")
        ) : (
          <>
            <Text className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              {current ? "Reassign to" : "Assign to"}
            </Text>
            {packers.map((person) => {
              const isCurrent = person.id === task.assignedTo;
              return (
                <Dropdown.Item key={person.id} disabled={isCurrent} onClick={() => void assign(person)} className="gap-2.5 text-sm">
                  {isCurrent ? <Check aria-hidden className="size-4 shrink-0 text-green-dark" /> : <span aria-hidden className="size-4 shrink-0" />}
                  <span className="min-w-0 text-start">
                    <span className="block truncate">{person.displayName ?? person.email ?? "Unknown user"}</span>
                    {person.email ? <span className="block truncate text-xs text-gray-500">{person.email}</span> : null}
                  </span>
                </Dropdown.Item>
              );
            })}
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

/* ── Progress ─────────────────────────────────────────────────────────────────────────────── */

/**
 * The tracker's six milestones. Each names the `ORDER_STATES` value that means it has been reached,
 * and the in-flight states on the way to it. Display grouping only — whether a move is legal is the
 * API's call, and nothing here decides one.
 */
const MILESTONES: { title: string; reachedAt: string; during: string[] }[] = [
  { title: "Placed", reachedAt: "placed", during: [] },
  { title: "Approved", reachedAt: "approved", during: ["awaiting_payment", "awaiting_company_approval", "awaiting_approval"] },
  { title: "Packed", reachedAt: "packed", during: ["picking_packing"] },
  { title: "Invoiced", reachedAt: "invoiced", during: [] },
  { title: "Dispatched", reachedAt: "dispatched", during: [] },
  { title: "Delivered", reachedAt: "delivered", during: [] },
];

type StepStatus = "waiting" | "in-progress" | "completed" | "error";

function progressSteps(order: OrderDetail) {
  const cancelled = order.state === "cancelled";
  // A cancelled order stopped wherever its cancelling move came from. With no such entry in its
  // history, all that is known is that it was placed — no step is marked as where it stopped.
  const cancelledFrom = cancelled ? [...order.history].reverse().find((entry) => entry.toState === "cancelled")?.fromState : undefined;
  const from = cancelled ? (cancelledFrom ?? "placed") : order.state;
  const index = MILESTONES.findIndex((m) => m.reachedAt === from || m.during.includes(from));
  const reached = index >= 0 && MILESTONES[index]!.reachedAt === from;
  const next = reached ? index + 1 : index; // the first milestone the order has not got past

  return MILESTONES.map((milestone, i) => {
    let status: StepStatus = i < next ? "completed" : "waiting";
    if (i === next && cancelledFrom) status = "error";
    else if (i === next && !cancelled && !reached) status = "in-progress";

    const reachedOn =
      milestone.reachedAt === "placed"
        ? order.placedAt
        : milestone.reachedAt === "approved" && order.approvedAt
          ? order.approvedAt
          : [...order.history].reverse().find((entry) => entry.toState === milestone.reachedAt)?.occurredAt;

    const description =
      status === "completed" ? (reachedOn ? formatDate(reachedOn) : "Done") : status === "in-progress" ? stateLabel(order.state) : status === "error" ? "Cancelled here" : undefined;

    return { ...milestone, status, description };
  });
}

/** Position, not state: done is green, under way is blue, where a cancellation stopped it is red. */
const STEP_COLOR: Record<StepStatus, "success" | "info" | "danger"> = {
  completed: "success",
  "in-progress": "info",
  error: "danger",
  waiting: "info",
};

function OrderProgress({ order }: { order: OrderDetail }) {
  const steps = progressSteps(order);
  const latest = order.history.at(-1);

  return (
    <WidgetCard
      rounded="xl"
      title={
        <CardTitle icon={<TruckIcon aria-hidden className="size-5" />} tile="bg-green-lighter text-green-dark">
          Order progress
        </CardTitle>
      }
    >
      <div className="-mx-1 mt-6 overflow-x-auto px-1 pb-2">
        <Stepper className="min-w-[720px]" titleClassName="text-sm font-semibold" descriptionClassName="text-xs">
          {steps.map((step) => (
            <Stepper.Step
              key={step.title}
              title={step.title}
              description={step.description}
              status={step.status}
              color={STEP_COLOR[step.status]}
              size="sm"
              icon={step.status === "in-progress" ? <InFlightIcon aria-hidden className="size-4" /> : undefined}
            />
          ))}
        </Stepper>
      </div>

      <div className="mt-5 rounded-lg border border-s-4 border-muted border-s-secondary bg-secondary-lighter/30 px-4 py-3 dark:bg-secondary-lighter/10">
        <Text className="text-xs font-semibold uppercase tracking-wider text-secondary-dark">Latest update</Text>
        {latest ? (
          <Text className="mt-1 text-sm text-gray-700">
            Moved from <strong className="font-semibold text-gray-900">{stateLabel(latest.fromState)}</strong> to{" "}
            <strong className="font-semibold text-gray-900">{stateLabel(latest.toState)}</strong> on {formatMoment(latest.occurredAt)}
            {latest.reason ? <> — “{latest.reason}”</> : null}
          </Text>
        ) : (
          <Text className="mt-1 text-sm text-gray-700">
            Placed on {formatMoment(order.placedAt)}. Its history carries no recorded state changes.
          </Text>
        )}
      </div>
    </WidgetCard>
  );
}

/* ── Flags ────────────────────────────────────────────────────────────────────────────────── */

function OrderFlags({ order, onSettle }: { order: OrderDetail; onSettle?: () => void }) {
  const flags: ReactNode[] = [];

  if (order.state === "cancelled") {
    flags.push(
      <Alert key="cancelled" color="danger" variant="flat" bar icon={<CancelIcon aria-hidden className="size-5" />}>
        <Text className="font-semibold text-red-dark">This order is cancelled</Text>
        <Text className="mt-0.5 text-sm">{order.cancellationReason ? <>Reason given: “{order.cancellationReason}”</> : "No reason was recorded against it."}</Text>
      </Alert>,
    );
  }
  if (order.shortFulfilmentPending) {
    flags.push(
      <Alert key="short-pending" color="warning" variant="flat" bar icon={<ScalesIcon aria-hidden className="size-5" />}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Text className="font-semibold text-orange-dark">Short fulfilment needs a decision</Text>
            <Text className="mt-0.5 text-sm">Less was packed than ordered. Ship it short, or hold it — nothing is invoiced until someone decides.</Text>
          </div>
          {onSettle ? (
            <Button type="button" size="sm" variant="outline" onClick={onSettle} className="border-orange-dark text-orange-dark hover:bg-orange-dark hover:text-white">
              Decide now
            </Button>
          ) : null}
        </div>
      </Alert>,
    );
  }
  if (order.shortFulfilmentDecision) {
    flags.push(
      <Alert key="short-settled" color="success" variant="flat" bar icon={<ApprovedIcon aria-hidden className="size-5" />}>
        <Text className="font-semibold text-green-dark">Short fulfilment settled</Text>
        <Text className="mt-0.5 text-sm">Decided as “{sentenceCase(order.shortFulfilmentDecision)}”.</Text>
      </Alert>,
    );
  }
  if (order.shortOnStock) {
    flags.push(
      <Alert key="short-stock" color="warning" variant="flat" bar icon={<WarningIcon aria-hidden className="size-5" />}>
        <Text className="font-semibold text-orange-dark">Short on stock</Text>
        <Text className="mt-0.5 text-sm">Accepted with free stock below the quantity ordered. A flag for the warehouse, not a refusal.</Text>
      </Alert>,
    );
  }
  if (order.reservationPending) {
    flags.push(
      <Alert key="reservation" color="info" variant="flat" bar icon={<InfoIcon aria-hidden className="size-5" />}>
        <Text className="font-semibold text-blue-dark">Reservation pending</Text>
        <Text className="mt-0.5 text-sm">Warehouse was unreachable when this was placed; the sweep will retry. Not a stock problem.</Text>
      </Alert>,
    );
  }

  if (flags.length === 0) return null;
  return <div className={cn("grid gap-3", flags.length > 1 && "@4xl:grid-cols-2")}>{flags}</div>;
}

/* ── Lines ────────────────────────────────────────────────────────────────────────────────── */

const AVATAR_COLORS = ["secondary", "info", "primary"] as const;

export function Lines({
  order,
  editable,
  busy,
  onSave,
  lockedReason,
}: {
  order: OrderDetail;
  editable: boolean;
  busy: boolean;
  onSave: (lines: { orderLineId: string; quantity: number }[]) => void;
  lockedReason?: string;
}) {
  // Row-by-row validation, not a calculation — the input is a quantity someone typed, not a
  // derived figure. An earlier version threw here, which crashed the submit instead of pointing at
  // the offending row.
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({});
  // A typed quantity saves itself after a pause (2026-09-30, owner: the line total and summary must
  // follow the quantity). The API re-prices; nothing is multiplied here (rule 1). One save per pause,
  // and none while a field is empty or every field still holds the saved quantity — each save moves
  // the reservation and writes an audit entry, so a no-op must not.
  const autoSave = useRef<number>(undefined);
  const formRef = useRef<HTMLFormElement>(null);
  // "Changed" is measured against what was last SENT and what the server last answered — never the
  // inputs' defaultValue, which is the order as last loaded and lags a save still in flight. Against
  // that, typing 2 then back to 1 while the save of 2 was running read as "unchanged", so the 1 was
  // never sent: the field showed 1 beside the server's line total for 2.
  const lastSent = useRef<string | null>(null);
  const busyNow = useRef(busy);
  busyNow.current = busy;
  const saved = order.lines.map((line) => `${line.lineId}=${Number(line.orderedQuantity)}`).join(";");

  /** What the fields hold, in the same shape as `saved`; null while one is empty or not a quantity. */
  function typedQuantities(form: HTMLFormElement): string | null {
    const parts: string[] = [];
    for (const input of form.querySelectorAll<HTMLInputElement>("input[data-line-id]")) {
      const amount = Number(input.value.trim());
      if (input.value.trim() === "" || !Number.isFinite(amount) || amount < 0) return null;
      parts.push(`${input.dataset.lineId}=${amount}`);
    }
    return parts.join(";");
  }

  /** One save at a time: anything typed while one runs is sent once it lands (the effect below). */
  function saveIfChanged(form: HTMLFormElement) {
    if (busyNow.current) return;
    const typed = typedQuantities(form);
    if (typed === null || typed === saved || typed === lastSent.current) return;
    form.requestSubmit();
  }

  // A save has landed (busy cleared, or the reload brought the server's quantities): send whatever
  // the fields still hold that the server does not.
  useEffect(() => {
    if (!busy && formRef.current) saveIfChanged(formRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- saveIfChanged reads refs and `saved`
  }, [busy, saved]);

  const title = (
    <CardTitle icon={<PackageIcon aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
      <span className="flex items-center gap-2">
        Order items
        <Badge variant="flat" color="secondary" rounded="pill" size="sm">
          {order.lines.length}
        </Badge>
      </span>
    </CardTitle>
  );
  const lockBadge = editable ? (
    <Badge variant="flat" color="success" rounded="pill" className="gap-1.5">
      <EditIcon aria-hidden className="size-3" />
      Editable
    </Badge>
  ) : (
    <Badge variant="flat" rounded="pill" className="gap-1.5 bg-gray-100 text-gray-600">
      <LockIcon aria-hidden className="size-3.5" />
      Locked
    </Badge>
  );

  if (order.lines.length === 0) {
    return (
      <WidgetCard rounded="xl" title={title} headerClassName="items-center">
        <div className="pt-5">
          <EmptyState title="No lines on this order">This order has no line items.</EmptyState>
        </div>
      </WidgetCard>
    );
  }

  const rows = (
    <div className="overflow-x-auto">
      <Table variant="modern" className={`w-full min-w-[680px] ${GRID_TABLE}`}>
        <caption className="sr-only">The lines on order {order.orderNumber}</caption>
        <Table.Header>
          <Table.Row className="hover:bg-transparent">
            <Table.Head className="ps-5 lg:ps-7">Product</Table.Head>
            <Table.Head className="text-right">Ordered</Table.Head>
            <Table.Head className="text-right">Packed</Table.Head>
            <Table.Head className="text-right">Rate</Table.Head>
            <Table.Head className="pe-5 text-right lg:pe-7">Line total</Table.Head>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {order.lines.map((line, index) => {
            const packed = line.actualQuantity !== null;
            const isShort = packed && Number(line.actualQuantity) < Number(line.orderedQuantity);
            return (
              <Table.Row key={line.lineId} className={cn("transition-colors hover:bg-gray-50 dark:hover:bg-gray-100/40", isShort && "bg-orange-lighter/20")}>
                <Table.Cell className="py-4 ps-5 lg:ps-7">
                  <div className="flex items-center gap-3">
                    <Avatar name={line.skuName} color={AVATAR_COLORS[index % AVATAR_COLORS.length]} rounded="md" size="md" />
                    <div className="min-w-0">
                      <Text className="line-clamp-2 font-medium text-gray-900">{line.skuName}</Text>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <Badge variant="outline" size="sm" rounded="md" className="border-muted font-mono text-[11px] text-gray-600">
                          {line.skuCode}
                        </Badge>
                        {line.gstRate !== undefined && line.gstRate !== null ? (
                          <Badge variant="flat" color="info" size="sm" rounded="md" className="text-[11px]">
                            GST {String(line.gstRate)}%
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell className="py-4 text-right">
                  {editable ? (
                    <div className="flex justify-end">
                      <Input
                        id={`quantity-${line.lineId}`}
                        aria-label={`Ordered ${line.unitCode} of ${line.skuName}`}
                        data-line-id={line.lineId}
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="any"
                        defaultValue={String(line.orderedQuantity)}
                        size="sm"
                        suffix={<span className="text-xs text-gray-500">{line.unitCode}</span>}
                        className="w-36"
                        inputClassName="text-right font-medium"
                        error={lineErrors[line.lineId]}
                      />
                    </div>
                  ) : (
                    <span className="font-medium tabular-nums text-gray-900">
                      {String(line.orderedQuantity)} <span className="text-xs text-gray-500">{line.unitCode}</span>
                    </span>
                  )}
                </Table.Cell>
                <Table.Cell className="py-4 text-right">
                  {packed ? (
                    <div className="flex flex-col items-end gap-1">
                      <span className={cn("font-medium tabular-nums", isShort ? "text-orange-dark" : "text-gray-900")}>
                        {String(line.actualQuantity)} <span className="text-xs text-gray-500">{line.unitCode}</span>
                      </span>
                      <Badge variant="flat" color={isShort ? "warning" : "success"} size="sm" rounded="pill">
                        {isShort ? "Short" : "Packed"}
                      </Badge>
                    </div>
                  ) : (
                    <Text as="span" className="text-sm text-gray-500">
                      Not packed yet
                    </Text>
                  )}
                </Table.Cell>
                <Table.Cell className="py-4 text-right">
                  <span className="tabular-nums text-gray-700">{formatMoney(money(line.rate))}</span>
                  <span className="block text-xs text-gray-500">per {line.unitCode}</span>
                </Table.Cell>
                <Table.Cell className="py-4 pe-5 text-right lg:pe-7">
                  <span className="font-semibold tabular-nums text-gray-900">{formatMoney(money(line.lineTotal))}</span>
                  {(money(line.discount) ?? 0) > 0 ? (
                    <span className="block text-xs text-green-dark">less {formatMoney(money(line.discount))} discount</span>
                  ) : null}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );

  const card = (children: ReactNode) => (
    <WidgetCard rounded="xl" title={title} action={lockBadge} className="overflow-hidden p-0 lg:p-0" headerClassName="items-center px-5 pb-4 pt-5 lg:px-7 lg:pt-6">
      {children}
    </WidgetCard>
  );

  if (!editable) {
    return card(
      <>
        {rows}
        <div className="flex items-center gap-2 border-t border-muted bg-gray-50 px-5 py-3.5 text-sm text-gray-600 lg:px-7 dark:bg-gray-100/40">
          <LockIcon aria-hidden className="size-4 shrink-0 text-gray-500" />
          {lockedReason ?? "Lines cannot be amended once picking has begun — someone has already pulled material."}
        </div>
      </>,
    );
  }

  return card(
    <form
      ref={formRef}
      aria-busy={busy}
      className={cn(busy && "pointer-events-none opacity-60 transition-opacity")}
      onInput={(e) => {
        const form = e.currentTarget;
        window.clearTimeout(autoSave.current);
        autoSave.current = window.setTimeout(() => saveIfChanged(form), 700);
      }}
      onSubmit={(e) => {
        e.preventDefault();
        // Never two saves at once — they could land in either order. The effect sends it after.
        if (busyNow.current) return;
        const inputs = Array.from(e.currentTarget.querySelectorAll<HTMLInputElement>("input[data-line-id]"));
        const errors: Record<string, string> = {};
        const lines: { orderLineId: string; quantity: number }[] = [];
        for (const input of inputs) {
          const lineId = input.dataset.lineId!;
          const typed = input.value.trim();
          const amount = Number(typed);
          if (typed === "" || !Number.isFinite(amount) || amount < 0) {
            errors[lineId] = typed === "" ? "Enter a quantity." : amount < 0 ? "A quantity cannot be negative." : "Enter a number.";
            continue;
          }
          lines.push({ orderLineId: lineId, quantity: amount });
        }
        setLineErrors(errors);
        if (Object.keys(errors).length > 0) return;
        lastSent.current = lines.map((line) => `${line.orderLineId}=${line.quantity}`).join(";");
        onSave(lines);
      }}
    >
      {rows}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-muted bg-secondary-lighter/30 px-5 py-3.5 lg:px-7 dark:bg-secondary-lighter/10">
        <Text className="flex items-center gap-2 text-sm text-gray-600">
          <InfoIcon aria-hidden className="size-4 shrink-0 text-secondary" />
          Saving re-prices the order and adjusts the reservation.
        </Text>
        <Button type="submit" size="sm" isLoading={busy}>
          Save quantities
        </Button>
      </div>
    </form>,
  );
}

/* ── Summary ──────────────────────────────────────────────────────────────────────────────── */

/**
 * The order's own figures, at the ordered quantities — Ordering's. The invoice bills the PACKED
 * quantities (BR-03), so after a short pack the two differ by design; the invoice's total is shown
 * under its own name in the footer, never in place of this one.
 */
function OrderSummary({ order, draft }: { order: OrderDetail; draft: InvoiceDetail | null }) {
  const deliveryDifference = money(order.deliveryChargeDifference);
  return (
    <section aria-labelledby="order-summary-title" className="overflow-hidden rounded-xl border border-muted bg-gray-0 dark:bg-gray-50">
      <div className="bg-gradient-to-br from-secondary to-blue px-6 py-5 text-white">
        <Text id="order-summary-title" className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/85">
          <RupeeIcon aria-hidden className="size-4" />
          Order total
        </Text>
        <Text className="mt-2 font-lexend text-3xl font-bold tabular-nums">{formatMoney(money(order.grandTotal))}</Text>
        <Text className="mt-1 text-sm text-white/85">
          {order.lines.length} {order.lines.length === 1 ? "line" : "lines"} · {order.orderType === "credit" ? "on credit" : "prepaid"} · as ordered
        </Text>
      </div>

      <dl className="flex flex-col gap-3 px-6 py-5 text-sm">
        <SummaryRow label="Subtotal" value={formatMoney(money(order.subtotal))} />
        {(money(order.discountTotal) ?? 0) > 0 ? (
          <SummaryRow
            label={
              <span className="flex items-center gap-2">
                Trade discount
                <Badge variant="flat" color="success" size="sm" rounded="pill">
                  {String(money(order.discountPercent) ?? 0)}%
                </Badge>
              </span>
            }
            value={<span className="text-green-dark">−{formatMoney(money(order.discountTotal))}</span>}
          />
        ) : null}
        <SummaryRow
          label={
            <span className="flex items-center gap-2">
              Delivery
              {order.deliveryChargeWaived ? (
                <Badge variant="flat" color="success" size="sm" rounded="pill">
                  Waived
                </Badge>
              ) : null}
            </span>
          }
          value={formatMoney(money(order.deliveryCharge))}
        />
        {deliveryDifference ? <SummaryRow label="Delivery difference, from a changed address" value={formatMoney(deliveryDifference)} /> : null}
        {/* D-04: an order mixing GST-inclusive and GST-exclusive lines shows the two parts. */}
        {order.taxIncluded || (money(order.taxIncludedTotal) ?? 0) === 0 ? (
          <SummaryRow
            label={order.taxIncluded ? "GST (included in subtotal)" : (money(order.discountTotal) ?? 0) > 0 ? "GST (on the discounted value)" : "GST"}
            value={formatMoney(money(order.taxTotal))}
          />
        ) : (
          <>
            <SummaryRow label="GST (included in subtotal)" value={formatMoney(money(order.taxIncludedTotal))} />
            <SummaryRow label="GST (added)" value={formatMoney(money(order.taxAddedTotal))} />
          </>
        )}
        <div className="flex items-baseline justify-between gap-4 border-t border-dashed border-muted pt-3">
          <dt className="text-base font-semibold text-gray-900">Order total</dt>
          <dd className="font-lexend text-lg font-bold tabular-nums text-gray-900">{formatMoney(money(order.grandTotal))}</dd>
        </div>
        {/* D-15: the refund Billing was asked for and what it has confirmed, each the API's own figure. */}
        {order.refundRequestedAmount != null ? <SummaryRow label="Refund requested" value={formatMoney(money(order.refundRequestedAmount))} /> : null}
        {order.refundedAmount != null ? <SummaryRow label="Refunded" value={formatMoney(money(order.refundedAmount))} /> : null}
        {/* FR-026: a person — by the name Ordering sends ("Unknown user" when it can't say, never the
            id) — or the component that approved it when no person did. */}
        {order.approvedByComponent ? (
          <SummaryRow label="Approved by" value={order.approvedByComponent} />
        ) : order.approvedBy ? (
          <SummaryRow label="Approved by" value={order.approvedByName ?? "Unknown user"} />
        ) : null}
      </dl>

      {order.invoiceNumber || draft ? (
        <div className="flex items-center justify-between gap-3 border-t border-muted bg-blue-lighter/30 px-6 py-4 text-sm dark:bg-blue-lighter/10">
          <span className="flex flex-col text-blue-dark">
            <span className="flex items-center gap-2">
              <ReceiptIcon aria-hidden className="size-4" />
              Invoice total · <strong className="font-semibold">{order.invoiceNumber ?? draft?.invoiceNumber}</strong>
              {order.invoiceNumber ? null : " (draft)"}
            </span>
            <span className="ms-6 text-xs text-gray-500">billed at the packed quantity</span>
          </span>
          <span className="font-semibold tabular-nums text-gray-900">
            {order.invoiceNumber ? formatMoney(money(order.invoicedTotal)) : draft?.grandTotalDisplay}
          </span>
        </div>
      ) : null}
    </section>
  );
}

function SummaryRow({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium tabular-nums text-gray-900">{value}</dd>
    </div>
  );
}

/* ── Customer & delivery ──────────────────────────────────────────────────────────────────── */

function addressText(address: CustomerAddress): string {
  return [address.line1, address.line2, address.city, stateName(address.stateCode), address.pincode].filter(Boolean).join(", ");
}

export function CustomerCard({
  order,
  customer,
  error,
  signature,
  onChangeAddress,
}: {
  order: OrderDetail;
  customer: CustomerDetail | null;
  error?: unknown;
  signature: string | null | undefined;
  /** Present only while the address may still change (before picking/packing); true when it was saved. */
  onChangeAddress?: (addressId: string, pincode: string) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const contact = customer?.users.find((user) => user.id === order.customerUserId);
  const shipping = customer?.addresses.find((address) => address.id === order.shippingAddressId);
  // Delivery is re-priced on the pincode, and the API refuses an address without one.
  const choices = (customer?.addresses ?? []).filter((a) => a.id && a.pincode && addressTypeLabel(a.type) === "shipping");
  const billing = order.billingAddressId ? customer?.addresses.find((address) => address.id === order.billingAddressId) : undefined;

  return (
    <WidgetCard
      rounded="xl"
      title={
        <CardTitle icon={<UserIcon aria-hidden className="size-5" />} tile="bg-blue-lighter text-blue-dark">
          Customer & delivery
        </CardTitle>
      }
      headerClassName="items-center"
    >
      <div className="mt-5 flex flex-col gap-5">
        {customer ? (
          <div className="flex items-start gap-3 rounded-xl bg-gradient-to-r from-blue-lighter/40 to-transparent p-3 dark:from-blue-lighter/10">
            <Avatar name={customer.legalName} color="info" size="lg" rounded="md" />
            <div className="min-w-0 flex-1">
              <Link to={customerPath(customer.summary.customerNumber)} className="line-clamp-2 font-semibold text-gray-900 hover:text-blue-dark hover:underline">
                {customer.legalName}
              </Link>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                {getStatusBadge(customerStatusLabel(customer.summary.status))}
                <Badge variant="outline" size="sm" rounded="md" className="border-muted font-mono text-[11px] text-gray-600">
                  {customer.gstin ? `GSTIN ${customer.gstin}` : "No GSTIN"}
                </Badge>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Alert color="warning" variant="flat" icon={<WarningIcon aria-hidden className="size-5" />}>
              <Text className="text-sm font-semibold">Could not read the customer record</Text>
              <Text className="mt-0.5 text-sm">{problemMessage(error, "The customers service did not answer.")}</Text>
            </Alert>
            <InfoBlock icon={<UserIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Customer account">
              <Link to={customerPath(order.customerId)} className="text-sm font-medium text-gray-900 hover:underline">
                {order.customerName ?? "Unknown customer"}
              </Link>
            </InfoBlock>
          </div>
        )}

        {contact ? (
          <InfoBlock icon={<MailIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="Placed by">
            <Text className="font-medium text-gray-900">{contact.name}</Text>
            <a href={`mailto:${contact.email}`} className="break-all text-sm text-gray-500 hover:text-green-dark hover:underline">
              {contact.email}
            </a>
          </InfoBlock>
        ) : (
          <InfoBlock icon={<MailIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="Placed by">
            <Text className="font-medium text-gray-900">{order.customerUserName ?? "Unknown user"}</Text>
          </InfoBlock>
        )}

        <div className="border-t border-dashed border-muted" />

        <InfoBlock
          icon={<MapPinIcon aria-hidden className="size-4" />}
          tile="bg-secondary-lighter text-secondary-dark"
          label="Ships to"
        >
          {editing && onChangeAddress ? (
            <form
              className="mt-1 flex flex-col gap-3"
              onSubmit={async (event) => {
                event.preventDefault();
                const chosen = choices.find((a) => a.id === new FormData(event.currentTarget).get("shippingAddressId"));
                if (!chosen) return;
                setSaving(true);
                if (await onChangeAddress(chosen.id!, chosen.pincode!)) setEditing(false);
                setSaving(false);
              }}
            >
              <SelectField label="New shipping address" hideLabel name="shippingAddressId" defaultValue={order.shippingAddressId ?? ""}>
                <option value="">Choose an address…</option>
                {choices.map((a) => (
                  <option key={a.id} value={a.id}>
                    {addressText(a)}
                  </option>
                ))}
              </SelectField>
              <Text className="text-xs text-gray-500">
                Delivery is re-priced for the new pincode; any difference is recorded on the order. The address is fixed once picking and packing starts.
              </Text>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="text" size="sm" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" isLoading={saving}>
                  Save address
                </Button>
              </div>
            </form>
          ) : (
            <>
              {shipping ? (
                <Text className="text-sm leading-relaxed text-gray-900">{addressText(shipping)}</Text>
              ) : (
                <Text className="text-sm text-gray-500">Address not on file</Text>
              )}
              {onChangeAddress && choices.length > 0 ? (
                <Button
                  type="button"
                  variant="text"
                  size="sm"
                  className="mt-1 h-auto gap-1.5 p-0 text-secondary-dark hover:underline"
                  onClick={() => setEditing(true)}
                >
                  <EditIcon aria-hidden className="size-3.5" />
                  Change address
                </Button>
              ) : null}
            </>
          )}
        </InfoBlock>

        {billing ? (
          <InfoBlock icon={<ReceiptIcon aria-hidden className="size-4" />} tile="bg-orange-lighter text-orange-dark" label="Bills to">
            <Text className="text-sm leading-relaxed text-gray-900">{addressText(billing)}</Text>
          </InfoBlock>
        ) : null}

        {order.delivery ? (
          <>
            <div className="border-t border-dashed border-muted" />
            <DeliveryRecord delivery={order.delivery} signature={signature} />
          </>
        ) : null}
      </div>
    </WidgetCard>
  );
}

/**
 * An In-House delivery: who received it, when, and the customer's signature, served by Files
 * (`GET /api/files/delivery-signatures/{hash}`, through the BFF with the session's token).
 * `signature` is `null` when none was recorded, `undefined` when the dispatch view could not be read.
 */
export function DeliveryRecord({
  delivery,
  signature,
}: {
  delivery: NonNullable<OrderDetail["delivery"]>;
  signature: string | null | undefined;
}) {
  const receiver = delivery.receiverName ?? "Receiver not recorded";

  return (
    <InfoBlock icon={<SignatureIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="Received by">
      <Text className="font-medium text-gray-900">{receiver}</Text>
      <Text className="text-sm text-gray-500">{formatMoment(delivery.completedAt)}</Text>
      {signature ? (
        <img
          src={imageUrl(signature, "/api/files/delivery-signatures")}
          alt={`Signature of ${receiver}`}
          className="mt-2 h-24 w-full max-w-xs rounded-lg border border-muted bg-white object-contain"
        />
      ) : (
        <Text className="mt-1 text-sm text-gray-500">
          {signature === undefined ? "Could not load the signature." : "No signature recorded."}
        </Text>
      )}
    </InfoBlock>
  );
}

/* ── Activity ─────────────────────────────────────────────────────────────────────────────── */

function ActivityFeed({ order }: { order: OrderDetail }) {
  const entries = [...order.history].reverse(); // newest first

  return (
    <WidgetCard
      rounded="xl"
      title={
        <CardTitle icon={<HistoryIcon aria-hidden className="size-5" />} tile="bg-orange-lighter text-orange-dark">
          Activity
        </CardTitle>
      }
      action={
        entries.length > 0 ? (
          <Badge variant="flat" color="warning" rounded="pill">
            {entries.length} {entries.length === 1 ? "change" : "changes"}
          </Badge>
        ) : null
      }
      headerClassName="items-center"
    >
      {entries.length === 0 ? (
        <div className="pt-5">
          <EmptyState title="No activity recorded">State changes appear here as they happen, with when and why.</EmptyState>
        </div>
      ) : (
        <ol className="mt-6 flex flex-col">
          {entries.map((entry, index) => {
            const latest = index === 0;
            const cancelled = entry.toState === "cancelled";
            return (
              <li key={`${entry.occurredAt}-${index}`} className="group relative flex gap-4 pb-6 last:pb-0">
                {index < entries.length - 1 ? <span aria-hidden className="absolute bottom-0 start-[17px] top-10 w-0.5 bg-muted" /> : null}
                <span
                  className={cn(
                    "relative z-10 flex size-9 shrink-0 items-center justify-center rounded-full ring-4 ring-gray-0 dark:ring-gray-50",
                    cancelled ? "bg-red text-white" : latest ? "bg-secondary text-white shadow-md" : "bg-green-lighter text-green-dark",
                  )}
                >
                  {cancelled ? <CancelIcon aria-hidden className="size-4" /> : latest ? <ArrowRight aria-hidden className="size-4" /> : <Check aria-hidden className="size-4" />}
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5 rounded-lg px-3 py-1.5 transition-colors group-hover:bg-gray-50 dark:group-hover:bg-gray-100/40">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-gray-500">{stateLabel(entry.fromState)}</span>
                      <ArrowRight aria-hidden className="size-3 text-gray-400" />
                      {getStatusBadge(entry.toState)}
                      {latest ? (
                        <Badge variant="flat" color="secondary" size="sm" rounded="pill">
                          Latest
                        </Badge>
                      ) : null}
                    </div>
                    <Text as="span" className="text-xs text-gray-500">
                      {formatMoment(entry.occurredAt)}
                    </Text>
                  </div>
                  {entry.reason ? <Text className="rounded-md border-s-2 border-muted bg-gray-50 px-3 py-2 text-sm italic text-gray-700 dark:bg-gray-100/40">“{entry.reason}”</Text> : null}
                  {entry.actorComponent || entry.actorId ? (
                    <Text className="text-xs text-gray-500">by {entry.actorComponent ?? entry.actorName ?? "Unknown user"}</Text>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </WidgetCard>
  );
}

function ShortFulfilmentConsequence({ order }: { order: OrderDetail }) {
  return (
    <>
      <strong>Ship short</strong> invoices this order at the packed quantity and proceeds it.{" "}
      {order.orderType === "credit" ? (
        <>It is a credit order, so nothing was paid and no refund arises — the customer is invoiced for what actually shipped.</>
      ) : (
        <>
          It is a prepaid order, so Infra Junction <strong>requests a refund of the difference</strong> to the customer. The refund
          itself is handled by the payments feature.
        </>
      )}{" "}
      <strong>Hold</strong> leaves the order held and raises no invoice. Both are recorded with the reason you give.
    </>
  );
}
